import { useQuery } from '@tanstack/react-query';

export interface GovernancaKpis {
  total_inscricoes: number;
  inscricoes_govbr: number;
  cobertura_govbr_pct: number;
  total_termos_ativos: number;
  termos_com_assinatura: number;
  cobertura_assinatura_pct: number;
  total_assinaturas: number;
  integridade_pct: number;
}

export interface GovernancaCobertura {
  operacao: string;
  selo_minimo: 'bronze' | 'prata' | 'ouro';
  ativo: boolean;
  descricao: string | null;
}

export interface GovernancaPolitica {
  tipo_documento: string;
  nivel_minimo: string;
  selo_govbr_minimo: string | null;
  base_legal: string;
  ativo: boolean;
  provedores: Array<{ provedor: string; prioridade: number; ativo: boolean }> | null;
}

export interface GovernancaTermosDre {
  dre: string;
  total: number;
  com_assinatura: number;
  percentual: number;
}

export interface GovernancaIntegridade {
  total_assinaturas: number;
  termos_integros: number;
  termos_quebrados: number;
  cadeias_quebradas: number;
  percentual_integro: number;
}

export interface GovernancaIndicadores {
  gerado_em: string;
  kpis: GovernancaKpis;
  govbr_cobertura: GovernancaCobertura[];
  politica: GovernancaPolitica[];
  termos_por_dre: GovernancaTermosDre[];
  integridade: GovernancaIntegridade;
}

/**
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Não existe
 * módulo de governança institucional em `apps/api/src/modulos/` (confirmado:
 * nenhum módulo expõe indicadores de Gov.BR/assinatura consolidados) —
 * decisão do dono de 2026-08-23, registrada em CLAUDE.md, seção "Proibido".
 * Os números abaixo são plausíveis para o domínio (cobertura Gov.BR e de
 * assinatura digital dos termos de adesão), mas não vêm de apuração real.
 * Some inteiro quando `GET /governanca/institucional/indicadores` existir.
 */
const GOVERNANCA_INSTITUCIONAL_MOCK: GovernancaIndicadores = {
  gerado_em: new Date().toISOString(),
  kpis: {
    total_inscricoes: 420,
    inscricoes_govbr: 318,
    cobertura_govbr_pct: 76,
    total_termos_ativos: 260,
    termos_com_assinatura: 214,
    cobertura_assinatura_pct: 82,
    total_assinaturas: 214,
    integridade_pct: 100,
  },
  govbr_cobertura: [
    {
      operacao: 'login_portal_credenciado',
      selo_minimo: 'prata',
      ativo: true,
      descricao: 'Exigido para acesso ao Portal do Transportador.',
    },
    {
      operacao: 'login_portal_admin',
      selo_minimo: 'ouro',
      ativo: true,
      descricao: 'Exigido para perfis administrativos da SMT/SME.',
    },
    {
      operacao: 'assinatura_termo',
      selo_minimo: 'ouro',
      ativo: true,
      descricao: 'Assinatura do termo de adesão ao credenciamento.',
    },
    {
      operacao: 'assinatura_aditivo',
      selo_minimo: 'ouro',
      ativo: true,
      descricao: 'Assinatura de aditivos contratuais.',
    },
    {
      operacao: 'assinatura_distrato',
      selo_minimo: 'ouro',
      ativo: false,
      descricao: 'Ainda em avaliação pela DTP.',
    },
    {
      operacao: 'inscricao_publica',
      selo_minimo: 'bronze',
      ativo: true,
      descricao: 'Etapa inicial de inscrição no chamamento público.',
    },
    {
      operacao: 'consulta_publica',
      selo_minimo: 'bronze',
      ativo: false,
      descricao: null,
    },
  ],
  politica: [
    {
      tipo_documento: 'termo',
      nivel_minimo: 'ouro',
      selo_govbr_minimo: 'ouro',
      base_legal: 'Contrato 019/SMT.GAB/2026, cláusula de credenciamento.',
      ativo: true,
      provedores: [
        { provedor: 'gov.br', prioridade: 1, ativo: true },
        { provedor: 'certificado_icp_brasil', prioridade: 2, ativo: true },
      ],
    },
    {
      tipo_documento: 'aditivo',
      nivel_minimo: 'ouro',
      selo_govbr_minimo: 'ouro',
      base_legal: 'Contrato 019/SMT.GAB/2026, cláusula de aditamento.',
      ativo: true,
      provedores: [{ provedor: 'gov.br', prioridade: 1, ativo: true }],
    },
    {
      tipo_documento: 'distrato',
      nivel_minimo: 'ouro',
      selo_govbr_minimo: 'ouro',
      base_legal: 'Contrato 019/SMT.GAB/2026, cláusula de rescisão.',
      ativo: false,
      provedores: null,
    },
    {
      tipo_documento: 'contrato',
      nivel_minimo: 'prata',
      selo_govbr_minimo: 'prata',
      base_legal: 'Lei 14.063/2020, assinaturas eletrônicas em atos públicos.',
      ativo: true,
      provedores: [{ provedor: 'gov.br', prioridade: 1, ativo: true }],
    },
    {
      tipo_documento: 'declaracao',
      nivel_minimo: 'bronze',
      selo_govbr_minimo: null,
      base_legal: 'Manual de Credenciamento SMT, anexo de declarações.',
      ativo: true,
      provedores: null,
    },
    {
      tipo_documento: 'parecer',
      nivel_minimo: 'bronze',
      selo_govbr_minimo: null,
      base_legal: 'Manual de Credenciamento SMT, anexo de pareceres.',
      ativo: true,
      provedores: null,
    },
  ],
  termos_por_dre: [
    { dre: 'DRE Exemplo Norte', total: 68, com_assinatura: 60, percentual: 88 },
    { dre: 'DRE Exemplo Sul', total: 74, com_assinatura: 58, percentual: 78 },
    { dre: 'DRE Exemplo Leste', total: 61, com_assinatura: 46, percentual: 75 },
    { dre: 'DRE Exemplo Oeste', total: 57, com_assinatura: 50, percentual: 88 },
  ],
  integridade: {
    total_assinaturas: 214,
    termos_integros: 214,
    termos_quebrados: 0,
    cadeias_quebradas: 0,
    percentual_integro: 100,
  },
};

export function useGovernancaInstitucional() {
  return useQuery<GovernancaIndicadores>({
    queryKey: ['governanca-institucional'],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => GOVERNANCA_INSTITUCIONAL_MOCK,
    staleTime: 60_000,
  });
}
